import { ProductPreOrderSettings, StockIndication } from '@spa/generated/graphql/graphql';
import { CurrencyIso } from '../converters/convertCurrencyIso';
import { ProductBadge } from '@spa/components/product-info/ProductBadge';

interface AnalyticsPrice {
  readonly valueWithoutTax: number;
  readonly currencyIso: CurrencyIso;
}

interface AnalyticsCategory {
  readonly name: string;
}

interface AnalyticsBrand {
  readonly name: string;
}

export interface AnalyticsProduct {
  readonly code: string;
  readonly name: string;
  readonly path: string;
  readonly campaignPrice: AnalyticsPrice | null;
  readonly currentPrice: AnalyticsPrice;
  readonly recommendedRetailPrice: AnalyticsPrice | null;
  readonly strikePrice: AnalyticsPrice | null;
  readonly brand: AnalyticsBrand | null;
  readonly categories: ReadonlyArray<AnalyticsCategory>;
  readonly categoryWithParents: ReadonlyArray<AnalyticsCategory>;
  readonly ownBrand: boolean | null;
  readonly quantity?: number;
  readonly index?: number;
  readonly badges?: ReadonlyArray<ProductBadge> | null;
  readonly stockLevelIndication?: StockIndication;
  readonly preOrderSettings?: ProductPreOrderSettings | null;
}

export type WishlistTrackingProduct = AnalyticsProduct & {
  readonly stockLevelIndication: StockIndication;
};

export interface AnalyticsItem {
  readonly affiliation?: string;
  readonly delivery?: AnalyticsCartDeliveryMethod;
  readonly index: number;
  readonly item_brand?: string;
  readonly item_category?: string;
  readonly item_category2?: string;
  readonly item_category3?: string;
  readonly item_category4?: string;
  readonly item_category5?: string;
  readonly item_id: string;
  readonly item_list_id?: string;
  readonly item_list_name?: string;
  readonly item_name: string;
  readonly item_variant?: string;
  readonly location_id?: string;
  readonly ownBrand: string;
  readonly price?: number;
  readonly quantity: number;
}

interface InteractionOrigin {
  originId: string;
  originType: string;
}

type AnalyticsCartDeliveryMethod = 'CC' | 'HD';

export default function convertProductsToAnalyticsItems(
  products: ReadonlyArray<AnalyticsProduct>,
  interactionOrigin?: InteractionOrigin,
  cartDeliveryMethod?: AnalyticsCartDeliveryMethod,
  affiliation?: string,
  suppressPriceData: boolean = false
): Array<AnalyticsItem> {
  return products.flatMap((product, arrayIndex) =>
    convertProductToAnalyticsItem(
      product,
      product.index ?? arrayIndex,
      interactionOrigin,
      cartDeliveryMethod,
      affiliation,
      suppressPriceData
    )
  );
}

export function convertProductToAnalyticsItem(
  {
    code,
    name,
    brand,
    campaignPrice,
    currentPrice,
    quantity,
    categoryWithParents,
    ownBrand
  }: AnalyticsProduct,
  index: number,
  interactionOrigin?: InteractionOrigin,
  cartDeliveryMethod?: AnalyticsCartDeliveryMethod,
  affiliation?: string,
  suppressPriceData: boolean = false
): AnalyticsItem {
  const categoryNames: Array<string> = categoryWithParents.map(
    ({ name }: AnalyticsCategory): string => name
  );

  return {
    affiliation,
    delivery: cartDeliveryMethod,
    index,
    item_brand: brand?.name,
    item_category: categoryNames[0],
    item_category2: categoryNames[1],
    item_category3: categoryNames[2],
    item_category4: categoryNames[3],
    item_category5: categoryNames[4],
    item_id: code,
    ...(interactionOrigin?.originId && interactionOrigin.originType
      ? {
          item_list_id: interactionOrigin.originId,
          item_list_name: interactionOrigin.originType
        }
      : {}),
    item_name: name,
    ownBrand: ownBrand ? 'true' : 'false',
    price: suppressPriceData
      ? undefined
      : campaignPrice
        ? campaignPrice.valueWithoutTax
        : currentPrice.valueWithoutTax,
    quantity: quantity ?? 1
  };
}
